import React, { useState } from 'react';
import { Phone, Copy, Trash2, RefreshCw } from 'lucide-react';

const FakePhoneGeneratorTool = () => {
  const [country, setCountry] = useState('us');
  const [count, setCount] = useState(5);
  const [format, setFormat] = useState('formatted'); // formatted, plain, e164
  const [phones, setPhones] = useState([]);
  const [copied, setCopied] = useState('');

  const countries = {
    us: { name: 'United States', code: '+1', format: '(XXX) XXX-XXXX', areaStart: 200, areaEnd: 999 },
    uk: { name: 'United Kingdom', code: '+44', format: '07XXX XXXXXX', areaStart: 700, areaEnd: 799 },
    ca: { name: 'Canada', code: '+1', format: '(XXX) XXX-XXXX', areaStart: 200, areaEnd: 999 },
    au: { name: 'Australia', code: '+61', format: '04XX XXX XXX', areaStart: 400, areaEnd: 499 },
    de: { name: 'Germany', code: '+49', format: '01XX XXXXXXX', areaStart: 150, areaEnd: 179 },
    fr: { name: 'France', code: '+33', format: '06 XX XX XX XX', areaStart: 60, areaEnd: 79 },
    in: { name: 'India', code: '+91', format: 'XXXXX XXXXX', areaStart: 70000, areaEnd: 99999 },
    br: { name: 'Brazil', code: '+55', format: '(XX) 9XXXX-XXXX', areaStart: 11, areaEnd: 99 },
  };

  const generateRandomDigits = (length) => {
    let result = '';
    for (let i = 0; i < length; i++) {
      result += Math.floor(Math.random() * 10);
    }
    return result;
  };

  const generatePhone = () => {
    const config = countries[country];
    let phone = '';
    
    switch (country) {
      case 'us':
      case 'ca':
        const areaCode = Math.floor(Math.random() * (config.areaEnd - config.areaStart + 1)) + config.areaStart;
        const exchange = Math.floor(Math.random() * 800) + 200; // Avoid 0XX and 1XX
        const subscriber = generateRandomDigits(4);
        phone = { 
          formatted: `(${areaCode}) ${exchange}-${subscriber}`,
          plain: `${areaCode}${exchange}${subscriber}`,
          e164: `${config.code}${areaCode}${exchange}${subscriber}`
        };
        break;
      case 'uk':
        const ukArea = generateRandomDigits(3);
        const ukNumber = generateRandomDigits(6);
        phone = {
          formatted: `07${ukArea} ${ukNumber}`,
          plain: `07${ukArea}${ukNumber}`,
          e164: `${config.code}7${ukArea}${ukNumber}`
        };
        break;
      case 'au':
        const auArea = Math.floor(Math.random() * 100) + 400;
        const auNum = generateRandomDigits(6);
        phone = {
          formatted: `0${auArea} ${auNum.substring(0,3)} ${auNum.substring(3)}`,
          plain: `0${auArea}${auNum}`,
          e164: `${config.code}${auArea}${auNum}`
        };
        break;
      case 'de':
        const deArea = Math.floor(Math.random() * 30) + 150;
        const deNum = generateRandomDigits(7);
        phone = {
          formatted: `0${deArea} ${deNum}`,
          plain: `0${deArea}${deNum}`,
          e164: `${config.code}${deArea}${deNum}`
        };
        break;
      case 'fr':
        const frArea = Math.floor(Math.random() * 20) + 60;
        const frNum = generateRandomDigits(8);
        phone = {
          formatted: `0${frArea} ${frNum.substring(0,2)} ${frNum.substring(2,4)} ${frNum.substring(4,6)} ${frNum.substring(6)}`,
          plain: `0${frArea}${frNum}`,
          e164: `${config.code}${frArea}${frNum}`
        };
        break;
      case 'in':
        const inArea = Math.floor(Math.random() * 30000) + 70000;
        const inNum = generateRandomDigits(5);
        phone = {
          formatted: `${inArea} ${inNum}`,
          plain: `${inArea}${inNum}`,
          e164: `${config.code}${inArea}${inNum}`
        };
        break;
      case 'br':
        const brArea = Math.floor(Math.random() * 89) + 11;
        const brNum = generateRandomDigits(8);
        phone = {
          formatted: `(${brArea}) 9${brNum.substring(0,4)}-${brNum.substring(4)}`,
          plain: `${brArea}9${brNum}`,
          e164: `${config.code}${brArea}9${brNum}`
        };
        break;
      default:
        phone = { formatted: '', plain: '', e164: '' };
    }
    
    return phone;
  };

  const generatePhones = () => {
    const newPhones = [];
    for (let i = 0; i < count; i++) {
      newPhones.push(generatePhone());
    }
    setPhones(newPhones);
  };

  const copyPhone = (phone, index) => {
    navigator.clipboard.writeText(phone[format]);
    setCopied(index);
    setTimeout(() => setCopied(''), 1500);
  };

  const copyAll = () => {
    const all = phones.map(p => p[format]).join('\n');
    navigator.clipboard.writeText(all);
    setCopied('all');
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
        <p className="text-sm text-yellow-300">
          These are randomly generated phone numbers for testing purposes only. 
          They may coincidentally match real phone numbers - do not use for any harmful purposes.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            {Object.entries(countries).map(([key, config]) => (
              <option key={key} value={key}>{config.name} ({config.code})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Count</label>
          <input
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
            min="1"
            max="50"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="formatted">Formatted</option>
            <option value="plain">Plain (digits only)</option>
            <option value="e164">E.164 (international)</option>
          </select>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={generatePhones}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
          <Phone size={18} />
          Generate Phone Numbers
        </button>
        <button
          onClick={generatePhones}
          disabled={phones.length === 0}
          className="flex items-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {phones.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-300">Generated Numbers</label>
            <button
              onClick={copyAll}
              className={`text-xs px-3 py-1 rounded transition-colors ${
                copied === 'all' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {copied === 'all' ? 'Copied!' : 'Copy All'}
            </button>
          </div>
          <div className="space-y-2">
            {phones.map((phone, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <span className="font-mono text-white">{phone[format]}</span>
                <button
                  onClick={() => copyPhone(phone, i)}
                  className={`p-2 rounded transition-colors ${
                    copied === i ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => setPhones([])}
        disabled={phones.length === 0}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default FakePhoneGeneratorTool;
